// server-only

import { CardDefinition, ContentMediaType } from '@/types';
import DATA from '@/data';

import { URL_S3 } from '@/helpers/constants';
import { LanguageOptions, ServiceType } from '@/types';
import { SERVICE_TYPES } from '@/helpers/enums';

function cardDefinitionMapper(
  item: any,
  serviceType: ServiceType,
  contentType: ContentMediaType,
) {
  return {
    id: item.id,
    name: item['title'],
    timestamp: new Date().getTime(),
    imageSrc: `${URL_S3}/${serviceType}/images/thumbnails/${contentType}/${item.id}.webp`,
    type: contentType,
    tags: item.tags,
    link: `/media/${item.id}`,
  } as CardDefinition;
}

// Find the media content data from data json and then reshape it (exercises, audios, ...), historically getVideos
export const getMediaData = (
  serviceType: ServiceType,
  selectedLanguage: LanguageOptions,
  contentType: ContentMediaType,
  tag?: string,
) => {
  const serviceData = DATA[serviceType]?.[selectedLanguage];
  if (!serviceData || !serviceData[contentType]) {
    throw new Error('Service data or medias not found');
  }

  return serviceData[contentType]
    .filter((item) => !tag || item.tags.includes(tag))
    .map((item) => cardDefinitionMapper(item, serviceType, contentType));
};

export const getMedia = (
  id: string,
  serviceType: ServiceType,
  selectedLanguage: LanguageOptions,
  contentType: ContentMediaType,
) => {
  const serviceData = DATA[serviceType]?.[selectedLanguage];
  if (!serviceData || !serviceData[contentType]) {
    throw new Error('Service data or media not found');
  }

  const exercise = serviceData[contentType].find((item) => item.id === id);
  if (exercise) {
    return cardDefinitionMapper(exercise, serviceType, contentType);
  }

  throw new Error(`Media with ID ${id} not found`);
};

export const getFrontPageMedia = (
  serviceType: ServiceType,
  selectedLanguage: LanguageOptions,
  contentType: ContentMediaType,
) => {
  let FRONT_PAGE_MEDIA: string[] = [];

  const FITNESS_VIDEOS = ['0107', '0077', '0109', '0193', '0146', '0104'];
  const YOGA_VIDEOS = ['69', '77', '88'];
  const PILATES_VIDEOS = ['2', '7', '12'];
  const MEDITATION_AUDIOS = ['M12', 'M08', 'M07'];
  const ASTROLOGY_AUDIOS = ['P33', 'P40', 'P52'];
  const LOVE_AUDIOS = ['P01', 'P10', 'P23'];
  const COOKING_VIDEOS = ['1', '10', '7'];

  if (serviceType === SERVICE_TYPES.FITNESS) {
    FRONT_PAGE_MEDIA = FITNESS_VIDEOS;
  } else if (serviceType === SERVICE_TYPES.YOGA) {
    FRONT_PAGE_MEDIA = YOGA_VIDEOS;
  } else if (serviceType === SERVICE_TYPES.PILATES) {
    FRONT_PAGE_MEDIA = PILATES_VIDEOS;
  } else if (serviceType === SERVICE_TYPES.MEDITATION) {
    FRONT_PAGE_MEDIA = MEDITATION_AUDIOS;
  } else if (serviceType === SERVICE_TYPES.ASTROLOGY) {
    FRONT_PAGE_MEDIA = ASTROLOGY_AUDIOS;
  } else if (serviceType === SERVICE_TYPES.LOVE) {
    FRONT_PAGE_MEDIA = LOVE_AUDIOS;
  } else if (serviceType === SERVICE_TYPES.COOKING) {
    FRONT_PAGE_MEDIA = COOKING_VIDEOS;
  }

  const serviceData = DATA[serviceType]?.[selectedLanguage];
  if (!DATA[serviceType]) {
    console.warn(`[Service Media] Missing serviceType in DATA: ${serviceType}`);
  }
  if (!DATA[serviceType]?.[selectedLanguage]) {
    console.warn(
      `[Service Media] Missing language in DATA[${serviceType}]: ${selectedLanguage}`,
    );
  }
  if (!serviceData || !serviceData[contentType]) {
    throw new Error('[Service Media] Service data or media not found');
  }

  return serviceData[contentType]
    .filter((item) => FRONT_PAGE_MEDIA.includes(String(item.id)))
    .map((item) => cardDefinitionMapper(item, serviceType, contentType));
};

export const getMediaS3Content = async (
  id: string,
  serviceType: ServiceType,
  selectedLanguage: LanguageOptions,
  contentType: ContentMediaType,
) => {
  let fileContents = '';
  let data = null;

  try {
    data = getMedia(id, serviceType, selectedLanguage, contentType);
  } catch (error) {
    console.error(
      '[Service Media] Error fetching media data from config:',
      error,
    );
  }

  const s3Url = `${URL_S3}/${serviceType}/${contentType}/${selectedLanguage}/${id}.md`;

  try {
    const response = await fetch(s3Url);
    if (!response.ok) {
      throw new Error(`[Service Media] HTTP error status: ${response.status}`);
    }

    fileContents = await response.text();
  } catch (error) {
    console.error(
      '[Service Media] Error fetching media content from S3:',
      error,
    );
  }

  return {
    id,
    data,
    fileContents,
  };
};
